import React, { useMemo } from "react";
import { observer, Observer } from "mobx-react";
import ExerciseSearchFloating from "./ExerciseSearchFloating";
import { useCoachStore } from "../../contexts/CoachStoreContext";

/**
 * GlobalExerciseSearchFloating
 *
 * A singleton wrapper for ExerciseSearchFloating that renders once at a high level
 * in the component tree (e.g., BlockView). Individual components
 * (ExerciseCard, WorkoutCard, SlotTextNew) register their search config via
 * the Coach store instead of rendering their own ExerciseSearchFloating instances.
 *
 * This dramatically reduces the number of ExerciseSearchFloating instances from
 * ~N (where N is the number of exercises) to just 1.
 *
 * Usage:
 * 1. Render <GlobalExerciseSearchFloating /> once at a parent level
 * 2. Components call openFloatingSearch(config) to open the search
 * 3. The config contains all props needed for ExerciseSearchFloating
 */

const GlobalExerciseSearchFloating = observer(() => {
  const coachStore = useCoachStore();

  // Get config from coach store - wrapped in Observer to react to changes
  return (
    <Observer>
      {() => {
        const isOpen = !!coachStore?.activeFloatingSearchId;
        const config = coachStore?.floatingSearchConfig;

        // Don't render anything if not open or no config
        if (!isOpen || !config) {
          return null;
        }

        return (
          <ExerciseSearchFloating
            isOpen={isOpen}
            onClose={config.onClose}
            referenceElement={config.referenceElement}
            portalRoot={config.portalRoot ?? null}
            onSelectExercise={config.onSelectExercise}
            instanceId={coachStore.activeFloatingSearchId}
            suggestionContext={config.suggestionContext ?? null}
            crossAxisOffset={config.crossAxisOffset ?? 0}
            initialSearchText={coachStore.floatingSearchInitialText || ""}
            slotExercise={config.slotExercise ?? null}
            primarySlotExercise={config.primarySlotExercise ?? null}
            block={config.block ?? null}
            protocolSlot={config.protocolSlot ?? null}
            currentWeek={config.currentWeek ?? null}
            suggestionsMode={config.suggestionsMode ?? "protocol"}
            currentWorkout={config.currentWorkout ?? null}
            exerciseIndex={config.exerciseIndex ?? 0}
            allowShiftEnterReopen={config.allowShiftEnterReopen ?? false}
            onRequestAddExercise={config.onRequestAddExercise ?? null}
          />
        );
      }}
    </Observer>
  );
});

export default GlobalExerciseSearchFloating;

/**
 * Helper function to open the global floating search
 * Call this from ExerciseCard, WorkoutCard, SlotTextNew instead of managing local state
 *
 * @param {object} coach - The coach store instance
 * @param {object} config - Configuration object with all ExerciseSearchFloating props
 * @param {string} config.instanceId - Unique identifier for this search instance
 * @param {HTMLElement} config.referenceElement - DOM element to anchor the floating search
 * @param {function} config.onSelectExercise - Callback when exercise is selected
 * @param {function} config.onClose - Callback when search is closed
 * @param {HTMLElement} [config.portalRoot] - Optional DOM root for FloatingPortal (useful inside modals)
 * @param {string} [config.suggestionContext] - Context for suggestions (e.g., "squat-string")
 * @param {number} [config.crossAxisOffset] - Cross axis offset for positioning
 * @param {string} [config.initialSearchText] - Initial text to populate search
 * @param {object} [config.slotExercise] - Current slot exercise being edited
 * @param {object} [config.primarySlotExercise] - Primary slot exercise
 * @param {object} [config.block] - Block reference
 * @param {string} [config.protocolSlot] - Protocol slot identifier
 * @param {number} [config.currentWeek] - Current week number
 * @param {string} [config.suggestionsMode] - "protocol" or "workout"
 * @param {object} [config.currentWorkout] - Current workout object
 * @param {number} [config.exerciseIndex] - Position of exercise in workout
 */
export const openFloatingSearch = (coach, config) => {
  if (!coach?.setProperty) return;

  const { instanceId, initialSearchText = "", ...restConfig } = config;

  // Close any existing search first
  if (coach.floatingSearchCloseCallback && coach.activeFloatingSearchId !== instanceId) {
    coach.floatingSearchCloseCallback();
  }

  // Store initial text
  coach.setProperty("floatingSearchInitialText", initialSearchText);
  
  // Store the full config
  coach.setProperty("floatingSearchConfig", restConfig);
  
  // Store the close callback
  coach.setProperty("floatingSearchCloseCallback", restConfig.onClose);
  
  // Open the search (this triggers the render)
  coach.setProperty("activeFloatingSearchId", instanceId);
};

/**
 * Helper function to close the global floating search
 *
 * @param {object} coach - The coach store instance
 * @param {string} [instanceId] - Optional instance ID to verify ownership
 */
export const closeFloatingSearch = (coach, instanceId = null) => {
  if (!coach?.setProperty) return;

  // If instanceId provided, only close if it matches
  if (instanceId && coach.activeFloatingSearchId !== instanceId) {
    return;
  }

  coach.setProperty("activeFloatingSearchId", null);
  coach.setProperty("floatingSearchCloseCallback", null);
  coach.setProperty("floatingSearchInitialText", "");
  coach.setProperty("floatingSearchConfig", null);
};
